import React from "react";
import { CheckIcon } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { StatusCounts } from "../../../data/project-progress";

type SegmentKey = "done" | "started" | "on_hold" | "todo";

interface SegmentConfig {
    key: SegmentKey;
    label: string;
    color: string;
    dotClassName: string;
}

const SEGMENTS: SegmentConfig[] = [
    { key: "done", label: "Done", color: "rgb(var(--status-done))", dotClassName: "bg-status-done" },
    { key: "started", label: "Started", color: "rgb(var(--status-started))", dotClassName: "bg-status-started" },
    { key: "on_hold", label: "On hold", color: "rgb(var(--status-on-hold))", dotClassName: "bg-status-on-hold" },
    { key: "todo", label: "Not started", color: "rgb(var(--status-todo))", dotClassName: "bg-status-todo" },
];

interface SizeConfig {
    diameter: number;
    strokeWidth: number;
    gapDegrees: number;
}

const SIZE_PRESETS: Record<"sm" | "default" | "lg", SizeConfig> = {
    sm: { diameter: 40, strokeWidth: 3, gapDegrees: 4 },
    default: { diameter: 48, strokeWidth: 3.5, gapDegrees: 3.5 },
    lg: { diameter: 56, strokeWidth: 4, gapDegrees: 3 },
};

interface ArcSegment {
    key: SegmentKey;
    color: string;
    dashArray: string;
    dashOffset: number;
}

function buildArcSegments(counts: StatusCounts, circumference: number, gapDegrees: number): ArcSegment[] {
    const nonZeroSegments = SEGMENTS.filter((s) => counts[s.key] > 0);

    if (nonZeroSegments.length <= 1) {
        return nonZeroSegments.map((s) => ({
            key: s.key,
            color: s.color,
            dashArray: `${circumference} 0`,
            dashOffset: 0,
        }));
    }

    const gapLength = (gapDegrees / 360) * circumference;
    const totalGap = gapLength * nonZeroSegments.length;
    const usableLength = circumference - totalGap;

    const arcs: ArcSegment[] = [];
    let offset = 0;

    for (const segment of nonZeroSegments) {
        const arcLength = (counts[segment.key] / counts.total) * usableLength;
        arcs.push({
            key: segment.key,
            color: segment.color,
            dashArray: `${arcLength} ${circumference - arcLength}`,
            dashOffset: -offset,
        });
        offset += arcLength + gapLength;
    }

    return arcs;
}

function StatusCircle({
    counts,
    size = "default",
    tooltipSide = "bottom",
    className,
    children,
}: {
    counts: StatusCounts;
    size?: "sm" | "default" | "lg";
    tooltipSide?: "top" | "right" | "bottom" | "left";
    className?: string;
    children?: React.ReactNode;
}) {
    const { diameter, strokeWidth, gapDegrees } = SIZE_PRESETS[size];
    const radius = (diameter - strokeWidth) / 2;
    const center = diameter / 2;
    const circumference = 2 * Math.PI * radius;
    const arcs = buildArcSegments(counts, circumference, gapDegrees);

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div
                    className={cn("relative inline-flex shrink-0 items-center justify-center", className)}
                    style={{ width: diameter, height: diameter }}
                >
                    <svg
                        width={diameter}
                        height={diameter}
                        viewBox={`0 0 ${diameter} ${diameter}`}
                        className="absolute inset-0"
                    >
                        <g transform={`rotate(-90 ${center} ${center})`}>
                            {arcs.map((arc) => (
                                <circle
                                    key={arc.key}
                                    cx={center}
                                    cy={center}
                                    r={radius}
                                    fill="none"
                                    strokeWidth={strokeWidth}
                                    strokeLinecap="butt"
                                    strokeDasharray={arc.dashArray}
                                    strokeDashoffset={arc.dashOffset}
                                    style={{
                                        stroke: arc.color,
                                        transition: "stroke-dasharray 0.3s ease, stroke-dashoffset 0.3s ease",
                                    }}
                                />
                            ))}
                        </g>
                    </svg>
                    {children && <div className="relative z-10">{children}</div>}
                </div>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide} className="max-w-[280px] p-0">
                <StatusTooltipContent counts={counts} />
            </TooltipContent>
        </Tooltip>
    );
}

function StatusTooltipContent({ counts }: { counts: StatusCounts }) {
    return (
        <div className="flex flex-col gap-2 px-3 py-2.5">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Task status &mdash; {counts.total} total
            </div>
            <div className="flex flex-col gap-1">
                {SEGMENTS.filter((segment) => counts[segment.key] > 0).map((segment) => {
                    const count = counts[segment.key];
                    const pct = counts.total > 0 ? Math.round((count / counts.total) * 100) : 0;
                    return (
                        <div key={segment.key} className="flex flex-row items-center gap-2 text-sm">
                            <span className={cn("h-2 w-2 shrink-0 rounded-full", segment.dotClassName)} />
                            <span className="flex-1 font-medium">{segment.label}</span>
                            <span className="w-6 text-right tabular-nums">{count}</span>
                            <span className="w-9 text-right tabular-nums text-muted-foreground">{pct}%</span>
                        </div>
                    );
                })}
            </div>
            <StatusBar counts={counts} />
        </div>
    );
}

function StatusBar({ counts, className }: { counts: StatusCounts; className?: string }) {
    if (counts.total === 0) return null;

    return (
        <div className={cn("flex h-1.5 w-full flex-row overflow-hidden rounded-full", className)}>
            {SEGMENTS.map((segment) => {
                const pct = (counts[segment.key] / counts.total) * 100;
                if (pct === 0) return null;
                return (
                    <div
                        key={segment.key}
                        className="transition-all duration-300 ease-in-out"
                        style={{ width: `${pct}%`, backgroundColor: segment.color }}
                    />
                );
            })}
        </div>
    );
}

function StatusDoneCheck({ className }: { className?: string }) {
    return <CheckIcon className={cn("h-5 w-5 shrink-0 text-status-done", className)} />;
}

export { StatusCircle, StatusTooltipContent, StatusBar, StatusDoneCheck };
